import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, MessageCircle, MoreHorizontal, Flag, Share2 } from 'lucide-react';
import { movementStoriesApi } from '../api/client';
import { useTranslation } from '../i18n';
import ReactionsSheet from './ReactionsSheet';
import CommentSection from './CommentSection';
import ReportModal from './ReportModal';
import ShareMovementStoryModal from './ShareMovementStoryModal';
import StoryAttribution from './StoryAttribution';
import './MovementStoryCard.css';

const REACTIONS = ['🙏', '❤️', '🔥', '🙌'];

// Comment API bound to a Movement story, passed into the shared CommentSection.
function movementCommentApi(storyId) {
  return {
    add:    (content, parentId) => movementStoriesApi.comment(storyId, content, parentId),
    remove: (commentId)         => movementStoriesApi.deleteComment(storyId, commentId),
  };
}

export default function MovementStoryCard({ story, onReact, showEngagement = false }) {
  const { t } = useTranslation('movement');
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(story.commentCount || 0);
  const [reactionSheet, setReactionSheet] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const reactions  = story.reactions || {};
  const myReaction = story.myReaction || null;

  const goToStory = () => navigate(`/movement/${story.id}`);

  return (
    <div className="ms-card">
      {/* Full-bleed cover with overlaid pills, menu, title & excerpt */}
      <div className={`ms-cover${story.coverImage ? '' : ' ms-cover--noimg'}`} onClick={goToStory} role="button">
        {story.coverImage && <img className="ms-cover-img" src={story.coverImage} alt="" loading="lazy" />}
        <div className="ms-cover-scrim" />

        <div className="ms-cover-top">
          {story.category && (
            <span className="ms-pill ms-pill-cat">{t(`categories.${story.category}`)}</span>
          )}
          {story.location && (
            <span className="ms-pill ms-pill-loc"><MapPin size={11} strokeWidth={2.25} /> {story.location}</span>
          )}
          <div className="ms-menu-wrap" onClick={e => e.stopPropagation()}>
            <button className="ms-cover-menu" onClick={() => setMenuOpen(o => !o)} aria-label={t('report')}>
              <MoreHorizontal size={17} />
            </button>
            {menuOpen && (
              <>
                <div className="ms-menu-backdrop" onClick={() => setMenuOpen(false)} />
                <div className="ms-menu">
                  <button onClick={() => { setMenuOpen(false); setShowShare(true); }}>
                    <Share2 size={14} /> {t('shareTitle')}
                  </button>
                  <button onClick={() => { setMenuOpen(false); setShowReport(true); }}>
                    <Flag size={14} /> {t('report')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="ms-cover-bottom">
          {story.title && <h3 className="ms-cover-title">{story.title}</h3>}
          {story.excerpt && <p className="ms-cover-excerpt">{story.excerpt}</p>}
          <StoryAttribution story={story} />
        </div>
      </div>

      {/* Engagement footer — hidden until the platform has enough activity to
          warrant showing reaction/comment counts. Reacting/commenting still
          happens on the detail page (/movement/:id). */}
      {showEngagement && (
        <div className="ms-footer">
          <div className="ms-actions">
            <div className="post-reactions">
              {REACTIONS.map(emoji => {
                const count = reactions[emoji] || 0;
                const mine  = myReaction === emoji;
                return (
                  <button key={emoji} className={`reaction-btn${mine ? ' mine' : ''}`}
                    onClick={() => onReact(story.id, emoji)}
                    onContextMenu={(e) => { e.preventDefault(); setReactionSheet({ filter: emoji }); }}>
                    {emoji}{count > 0 && <span>{count}</span>}
                  </button>
                );
              })}
              <button className="ms-reactors-link" onClick={() => setReactionSheet({ filter: null })}>
                {t('whoReacted')}
              </button>
            </div>
            <button className={`comment-toggle-btn${showComments ? ' open' : ''}`} onClick={() => setShowComments(s => !s)}>
              <MessageCircle size={15} strokeWidth={2} />
              <span>{commentCount}</span>
            </button>
          </div>
        </div>
      )}

      {showEngagement && showComments && (
        <CommentSection
          targetId={story.id}
          commentApi={movementCommentApi(story.id)}
          initialComments={story.comments || []}
          onCountChange={delta => setCommentCount(c => Math.max(0, c + delta))}
        />
      )}

      {reactionSheet && (
        <ReactionsSheet scope="movement" id={story.id} initialFilter={reactionSheet.filter}
          initialCounts={reactions} onClose={() => setReactionSheet(null)} />
      )}
      {showReport && (
        <ReportModal contentType="movement_story" contentId={story.id}
          onClose={() => setShowReport(false)} />
      )}
      {showShare && (
        <ShareMovementStoryModal story={story} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}
